/**
 * Mutual exclusion helpers for audio vs video playback.
 * Last media the user starts wins; the other must stop.
 * If a video interrupted playing audio, that audio auto-resumes when
 * the video is paused, ended, or closed — unless the video is only
 * handing off between the inline player and PiP.
 */

type PauseVideoArgs = {
  playerRef?: { current: any };
  setPlayingVideo?: (v: boolean) => void;
  setActiveMedia?: (v: "none" | "audio" | "video") => void;
};

type PauseAudioArgs = {
  refAudio?: { current: any };
  setPlayingAudio?: (v: boolean) => void;
  setActiveMedia?: (v: "none" | "audio" | "video") => void;
};

type ResumeAudioArgs = PauseAudioArgs;

/** True when pauseAudioForVideo stopped a track that was actually playing. */
let audioInterruptedByVideo = false;

/** Ignore resume until this timestamp (inline ↔ PiP handoff). */
let skipResumeUntil = 0;

/** Mark the next few seconds as a player handoff so audio does not blip back on. */
export const skipAudioResumeForHandoff = (durationMs = 2500): void => {
  const logdetails: string[] = [];
  logdetails.push("action=skip_audio_resume_for_handoff");
  logdetails.push(`durationMs=${durationMs}`);
  skipResumeUntil = Date.now() + durationMs;
  console.log("mediaMutex ->", logdetails.join(" | "));
};

/** Delay before auto-resume; stretches to cover an active handoff window. */
export const getAudioResumeDelayMs = (minDelayMs = 600): number => {
  return Math.max(minDelayMs, skipResumeUntil - Date.now());
};

/** User closed / replaced audio — do not auto-restart it later. */
export const clearAudioInterrupt = (): void => {
  const logdetails: string[] = [];
  logdetails.push("action=clear_audio_interrupt");
  logdetails.push(`wasInterrupted=${audioInterruptedByVideo}`);
  audioInterruptedByVideo = false;
  console.log("mediaMutex ->", logdetails.join(" | "));
};

/** Stop YouTube (inline or PiP) when audio starts. */
export const pauseVideoForAudio = ({
  playerRef,
  setPlayingVideo,
  setActiveMedia,
}: PauseVideoArgs): void => {
  const logdetails: string[] = [];
  logdetails.push("action=pause_video_for_audio");

  const player = playerRef?.current;
  if (player && typeof player.pauseVideo === "function") {
    player.pauseVideo();
    logdetails.push("result=youtube_paused");
  } else {
    logdetails.push("result=no_youtube_player");
  }

  setPlayingVideo?.(false);
  setActiveMedia?.("audio");

  console.log("mediaMutex ->", logdetails.join(" | "));
};

/** Stop bottom-bar / shared audio when video starts. */
export const pauseAudioForVideo = ({
  refAudio,
  setPlayingAudio,
  setActiveMedia,
}: PauseAudioArgs): void => {
  const logdetails: string[] = [];
  logdetails.push("action=pause_audio_for_video");

  const audioEl = refAudio?.current;
  const wasPlaying =
    !!audioEl &&
    typeof audioEl.pause === "function" &&
    audioEl.paused === false &&
    audioEl.ended === false;

  if (wasPlaying) {
    audioInterruptedByVideo = true;
    logdetails.push("interrupted=true");
  } else {
    logdetails.push("interrupted=unchanged");
    logdetails.push(`flag=${audioInterruptedByVideo}`);
  }

  if (audioEl && typeof audioEl.pause === "function") {
    audioEl.pause();
    logdetails.push("result=audio_paused");
  } else {
    logdetails.push("result=no_audio_element");
  }

  setPlayingAudio?.(false);
  setActiveMedia?.("video");

  console.log("mediaMutex ->", logdetails.join(" | "));
};

/**
 * Restart the shared audio element after video stops, but only when
 * that audio was playing at the moment the video took over.
 */
export const resumeAudioAfterVideo = ({
  refAudio,
  setPlayingAudio,
  setActiveMedia,
}: ResumeAudioArgs): void => {
  const logdetails: string[] = [];
  logdetails.push("action=resume_audio_after_video");

  if (Date.now() < skipResumeUntil) {
    logdetails.push("result=skipped_handoff");
    console.log("mediaMutex ->", logdetails.join(" | "));
    return;
  }

  if (!audioInterruptedByVideo) {
    logdetails.push("result=skipped_not_interrupted");
    console.log("mediaMutex ->", logdetails.join(" | "));
    return;
  }

  const audioEl = refAudio?.current;
  if (!audioEl || typeof audioEl.play !== "function") {
    audioInterruptedByVideo = false;
    logdetails.push("result=no_audio_element");
    console.log("mediaMutex ->", logdetails.join(" | "));
    return;
  }

  audioInterruptedByVideo = false;
  const playResult = audioEl.play();

  const onResumed = () => {
    setPlayingAudio?.(true);
    setActiveMedia?.("audio");
  };

  if (playResult && typeof playResult.then === "function") {
    playResult
      .then(() => {
        onResumed();
        logdetails.push("result=audio_resumed");
        console.log("mediaMutex ->", logdetails.join(" | "));
      })
      .catch((err: any) => {
        logdetails.push("result=play_failed");
        logdetails.push(`error=${err?.message || "unknown"}`);
        console.warn("mediaMutex ->", logdetails.join(" | "));
      });
    return;
  }

  onResumed();
  logdetails.push("result=audio_resumed");
  console.log("mediaMutex ->", logdetails.join(" | "));
};
